import { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { chapters, categories } from "@/mocks/chapters";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";

export default function CategoryPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || "all");

  const filteredChapters = useMemo(() => {
    if (activeCategory === "all") return chapters;
    return chapters.filter((ch) => ch.categorySlug === activeCategory);
  }, [activeCategory]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  const allCategories = [{ name: t("category.all"), slug: "all", icon: "ri-apps-line", description: "" }, ...categories];

  const pageTitle = currentCategory ? `${currentCategory.name} - 火影漫画` : "全部分类 - 火影漫画";

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <Navbar />

      <main className="pt-20 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <Breadcrumb
            items={[
              { label: t("breadcrumb.home"), href: "/" },
              { label: currentCategory ? currentCategory.name : t("category.title") },
            ]}
          />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              {currentCategory ? currentCategory.name : t("category.title")}
            </h1>
            {currentCategory && (
              <p className="text-sm text-foreground-600">{currentCategory.description}</p>
            )}
          </div>

          {/* Category Tabs */}
          <div className="mb-8 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
            <div className="flex items-center gap-2 min-w-max pb-2">
              {allCategories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => {
                    setActiveCategory(cat.slug);
                    window.history.replaceState(null, "", `/category/${cat.slug}`);
                  }}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {cat.icon && <i className={`${cat.icon} text-xs`}></i>}
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Chapter Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
            {filteredChapters.map((ch) => (
              <a
                key={ch.id}
                href={`/article/${ch.slug}`}
                className="group flex gap-4 p-4 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-300 transition-all duration-300"
              >
                <div className="w-20 h-28 flex-shrink-0 rounded-md overflow-hidden">
                  <img
                    src={ch.coverImage}
                    alt={ch.title}
                    title={ch.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-col justify-between min-w-0">
                  <div>
                    <span className="text-xs text-accent-500 font-medium">
                      {ch.category}
                    </span>
                    <h3 className="text-sm font-semibold text-foreground-900 mt-1 mb-1 line-clamp-2 leading-snug">
                      {ch.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                      {ch.summary}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-foreground-400 mt-2">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {ch.views.toLocaleString()} {t("readCount")}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {ch.publishDate}
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>

          {filteredChapters.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-background-100 rounded-full">
                <i className="ri-book-open-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">{t("category.noChapters")}</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}